import React, { useCallback, useId, useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from './pilot-ui/select';
import { Device, DeviceEnrichmentMap, DeviceEnrichment } from '../types';
import { clipFilterName, clipLabel } from '../utils/clipLabel';

interface DeviceSelectorProps {
  devices: Device[];
  /** Full list used to name a selected device even after it leaves the options. */
  allDevices?: Device[];
  separatorBeforeIds?: ReadonlySet<number>;
  labelMode?: 'full' | 'pin';
  enrichmentMap?: DeviceEnrichmentMap;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  className?: string;
  name?: string;
  placeholder?: string;
  /** If 'id', option values are the device id as string; if 'name', option values are device.name */
  valueMode?: 'name' | 'id';
  /** Allow the full selected label to wrap. */
  wrapSelectedLabel?: boolean;
  describedBy?: string;
  invalid?: boolean;
  disabled?: boolean;
  helpText?: string;
}

/**
 * Build a map from user ID to a sorted list of device names owned by that user.
 * Used to compute "#N of M" for enriched devices.
 */
export function buildUserDeviceIndex(enrichmentMap: DeviceEnrichmentMap): Record<string, string[]> {
  const byUser: Record<string, string[]> = {};
  Object.entries(enrichmentMap).forEach(([deviceName, enrichment]) => {
    const userId = enrichment.user?.id;
    if (!userId) return;
    if (!byUser[userId]) byUser[userId] = [];
    byUser[userId].push(deviceName);
  });
  // Sort each user's devices by name for a stable order
  Object.values(byUser).forEach(list => list.sort());
  return byUser;
}

/**
 * Format the display label for a device in a dropdown.
 *
 * The RDS `description` is the admin-curated label for the physical unit and
 * is treated as authoritative. The Firestore enrichment `user` is whoever
 * currently owns the device in the app — which can differ from the admin
 * label when a unit is physically reassigned or re-registered under a new
 * account. We surface BOTH so the mismatch is visible rather than silently
 * masked (previously the app owner overrode the description entirely).
 *
 * Priority:
 *   1. description + app owner: "{name} ({id}) — {description} [app: {email, name or ID}]"
 *   2. description only:        "{name} ({id}) — {description}"
 *   3. app owner only:          "{name} ({id}) — {email, name or ID} (#N of M)"
 *   4. Fallback:                "{name} ({id})"
 *
 * FS-825: the description is the owner's name, the same on every Clip in the
 * home, so the Clip's filter name (or its slot, utils/clipLabel) follows it
 * after a "·": "{name} ({id}) — {description} · Upstairs Loft". With no
 * description it follows whatever stands in for one.
 */
export function formatDeviceLabel(
  device: Device,
  enrichment: DeviceEnrichment | undefined,
  userDeviceIndex: Record<string, string[]>
): string {
  // Prominent text marker so decommissioned devices stand out in any dropdown.
  const prefix = device.decommissioned ? '⛔ DECOMMISSIONED — ' : '';
  const base = `${prefix}${device.name} (${device.id})`;

  let userLabel = '';
  let indexSuffix = '';
  if (enrichment?.user) {
    const { email, firstname, lastName, id } = enrichment.user;
    userLabel = email || [firstname, lastName].filter(Boolean).join(' ').trim() || id || '';
    const userId = enrichment.user.id;
    if (userId && userDeviceIndex[userId]) {
      const list = userDeviceIndex[userId];
      const position = list.indexOf(device.name) + 1;
      if (position > 0) {
        indexSuffix = ` (#${position} of ${list.length})`;
      }
    }
  }

  if (device.description) {
    const label = clipLabel({ ...device, name: null });
    return userLabel ? `${base} — ${label} [app: ${userLabel}]` : `${base} — ${label}`;
  }

  const filterName = clipFilterName(device);
  const tail = filterName ? ` · ${filterName}` : '';
  if (userLabel) {
    return `${base} — ${userLabel}${indexSuffix}${tail}`;
  }

  return `${base}${tail}`;
}

const formatPinDeviceLabel = (device: Device): string => {
  const descriptionAndFilter = clipLabel({ ...device, name: null });
  return descriptionAndFilter ? `${device.id} — ${descriptionAndFilter}` : String(device.id);
};

const DEFAULT_CLASSNAME = 'w-full max-w-md p-2 border rounded-md';

const DeviceSelector: React.FC<DeviceSelectorProps> = ({
  devices,
  allDevices = devices,
  separatorBeforeIds,
  labelMode = 'full',
  enrichmentMap = {},
  value,
  onChange,
  className = DEFAULT_CLASSNAME,
  name,
  placeholder = 'Select a device',
  valueMode = 'name',
  wrapSelectedLabel = false,
  describedBy,
  invalid = false,
  disabled = false,
  helpText = 'Choose the device this action applies to.',
}) => {
  const userDeviceIndex = useMemo(
    () => buildUserDeviceIndex(enrichmentMap),
    [enrichmentMap]
  );

  const labelFor = useCallback((device: Device) => (
    labelMode === 'pin'
      ? formatPinDeviceLabel(device)
      : formatDeviceLabel(device, enrichmentMap[device.name], userDeviceIndex)
  ), [enrichmentMap, labelMode, userDeviceIndex]);
  const valueFor = useCallback(
    (device: Device) => (valueMode === 'id' ? String(device.id) : device.name),
    [valueMode]
  );

  const options = useMemo(() => {
    return devices.map(device => ({
      id: device.id,
      value: valueFor(device),
      label: labelFor(device),
    }));
  }, [devices, labelFor, valueFor]);

  const excludedDevices = useMemo(() => {
    const rowIds = new Set(devices.map(device => device.id));
    return allDevices.filter(device => !rowIds.has(device.id));
  }, [allDevices, devices]);

  const selectedDevice = allDevices.find(device => valueFor(device) === value);
  const selectedLabel = selectedDevice ? labelFor(selectedDevice) : placeholder;

  const helpId = useId();
  // The analysis page also has a visible label for name="device".
  // One explicit reference prevents a doubled accessible name.
  const labelId = useId();
  const generatedFieldId = useId();
  const fieldId = name ?? generatedFieldId;
  const selectedExcluded = selectedDevice !== undefined && excludedDevices.includes(selectedDevice);
  const items = useMemo(() => [
    { value: '', label: placeholder },
    ...options.map(({ value: optionValue, label }) => ({ value: optionValue, label })),
    ...(selectedExcluded ? [{ value, label: selectedLabel }] : []),
  ], [options, placeholder, selectedExcluded, selectedLabel, value]);

  const handleValueChange = (next: string | null) => {
    // Existing consumers accept a React select change event. Dispatch a native
    // change on a real select so target/currentTarget retain that interface.
    const proxy = document.createElement('select');
    proxy.name = name ?? '';
    proxy.add(new Option('', next ?? ''));
    proxy.value = next ?? '';
    proxy.addEventListener('change', event => {
      let propagationStopped = false;
      const changeEvent: React.ChangeEvent<HTMLSelectElement> = {
        nativeEvent: event,
        currentTarget: proxy,
        target: proxy,
        bubbles: event.bubbles,
        cancelable: event.cancelable,
        defaultPrevented: event.defaultPrevented,
        eventPhase: event.eventPhase,
        isTrusted: event.isTrusted,
        preventDefault: () => event.preventDefault(),
        isDefaultPrevented: () => event.defaultPrevented,
        stopPropagation: () => { propagationStopped = true; event.stopPropagation(); },
        isPropagationStopped: () => propagationStopped,
        persist: () => undefined,
        timeStamp: event.timeStamp,
        type: event.type,
      };
      onChange(changeEvent);
    });
    proxy.dispatchEvent(new Event('change', { bubbles: true }));
  };

  return (
    <div>
      <label id={labelId} htmlFor={fieldId} className="sr-only">Device</label>
      <Select name={name} value={value} items={items} onValueChange={handleValueChange} disabled={disabled}>
        <SelectTrigger
          id={fieldId}
          aria-labelledby={labelId}
          aria-describedby={`${helpId}${describedBy ? ` ${describedBy}` : ''}`}
          aria-invalid={invalid || undefined}
          className={`${className} ${wrapSelectedLabel ? 'data-[size=default]:h-auto min-h-10 whitespace-normal *:data-[slot=select-value]:line-clamp-none *:data-[slot=select-value]:whitespace-normal' : ''}`}
          title={selectedDevice ? selectedLabel : 'Choose a device for this action'}
        >
          <SelectValue>{selectedLabel}</SelectValue>
        </SelectTrigger>
        {/* A hidden selected row has no position to align with the trigger. */}
        <SelectContent listLabel="Device" alignItemWithTrigger={!selectedExcluded}>
          <SelectItem value="">{placeholder}</SelectItem>
          {options.map(option => (
            <React.Fragment key={option.id}>
              {separatorBeforeIds?.has(option.id) && <SelectSeparator />}
              <SelectItem value={option.value}>
                {option.label}
              </SelectItem>
            </React.Fragment>
          ))}
          {/* Base UI clears a selected value that no item carries, and it
              checks against the previous value in the render where the
              selection moves off a Clip left out of the rows. Every such Clip
              therefore stays registered here, hidden and disabled so that
              neither typeahead nor arrow keys reach it. */}
          {excludedDevices.map(device => (
            <SelectItem key={device.id} value={valueFor(device)} disabled hidden>
              {labelFor(device)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p id={helpId} className="mt-1 text-xs text-muted-foreground">{helpText}</p>
    </div>
  );
};

export default DeviceSelector;
